import { diffTrees, NodeTree, OpError } from './tree.js';
import type { NodeId, NodeSnapshot, Op, OpBatch, SequencedBatch, TreeEvent } from './types.js';

export interface ReplicaChange {
  /** 'visible' is what the UI shows (local writes included); 'persisted' is server-confirmed state. */
  layer: 'visible' | 'persisted';
  /** True when the change came from this client's own local write. */
  local: boolean;
  events: TreeEvent[];
}

export type ReplicaListener = (change: ReplicaChange) => void;

/**
 * Client-side copy of a node database with optimistic local writes.
 *
 * `visible` = `persisted` + pending local batches. Local writes show up at once;
 * the server's ordered stream is applied to `persisted`. When a remote batch
 * arrives while local batches are still pending, `visible` is rebuilt by replaying
 * them over the new `persisted`, so local edits keep "shading" remote values
 * until the server confirms or rejects them.
 */
export class Replica {
  persisted: NodeTree;
  visible: NodeTree;
  private seq: number;
  private pending: OpBatch[] = [];
  private clientSeq = 0;
  private idCounter = 0;
  private readonly listeners = new Set<ReplicaListener>();

  constructor(readonly clientId: string, snapshot: NodeSnapshot, seq = 0) {
    this.persisted = NodeTree.fromSnapshot(snapshot);
    this.visible = this.persisted.clone();
    this.seq = seq;
  }

  get lastSeq(): number {
    return this.seq;
  }

  get pendingCount(): number {
    return this.pending.length;
  }

  on(listener: ReplicaListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** A node id that no other client can generate. */
  newId(): NodeId {
    return `${this.clientId}.${(++this.idCounter).toString(36)}`;
  }

  /**
   * Apply a local batch to `visible` and return it for sending. Throws (and
   * leaves `visible` untouched) if any op cannot apply locally.
   */
  local(ops: Op[]): OpBatch {
    const trial = this.visible.clone();
    for (const op of ops) trial.apply(op);
    const events = ops.flatMap((op) => this.visible.apply(op));
    const batch: OpBatch = { origin: this.clientId, clientSeq: ++this.clientSeq, ops };
    this.pending.push(batch);
    this.emit({ layer: 'visible', local: true, events });
    return batch;
  }

  /** A batch ordered by the server (ours being confirmed, or someone else's). */
  receive(batch: SequencedBatch): void {
    if (batch.seq <= this.seq) return;
    this.seq = batch.seq;
    const persistedEvents = applyAll(this.persisted, batch.ops);
    this.emit({ layer: 'persisted', local: batch.origin === this.clientId, events: persistedEvents });

    const mine = batch.origin === this.clientId && this.pending[0]?.clientSeq === batch.clientSeq;
    if (mine) {
      this.pending.shift();
      return;
    }
    if (this.pending.length === 0) {
      this.emit({ layer: 'visible', local: false, events: applyAll(this.visible, batch.ops) });
    } else {
      this.rebuildVisible(false);
    }
  }

  /** The server refused one of our batches: drop it and show the state without it. */
  reject(clientSeq: number): void {
    const i = this.pending.findIndex((b) => b.clientSeq === clientSeq);
    if (i < 0) return;
    this.pending.splice(i, 1);
    this.rebuildVisible(false);
  }

  /** Replace everything with a fresh server snapshot (after reconnecting or a visibility change). */
  resync(snapshot: NodeSnapshot, seq: number): void {
    const before = this.persisted;
    this.persisted = NodeTree.fromSnapshot(snapshot);
    this.seq = seq;
    this.emit({ layer: 'persisted', local: false, events: diffTrees(before, this.persisted) });
    this.rebuildVisible(false);
  }

  /** Pending batches to resend after a reconnection. */
  unconfirmed(): OpBatch[] {
    return [...this.pending];
  }

  private rebuildVisible(local: boolean): void {
    const next = this.persisted.clone();
    this.pending = this.pending.filter((b) => {
      const trial = next.clone();
      try {
        for (const op of b.ops) trial.apply(op);
      } catch (e) {
        if (e instanceof OpError) return false; // no longer applicable: drop it
        throw e;
      }
      for (const op of b.ops) next.apply(op);
      return true;
    });
    const events = diffTrees(this.visible, next);
    this.visible = next;
    if (events.length) this.emit({ layer: 'visible', local, events });
  }

  private emit(change: ReplicaChange): void {
    if (change.events.length === 0) return;
    for (const l of this.listeners) l(change);
  }
}

function applyAll(tree: NodeTree, ops: Op[]): TreeEvent[] {
  return ops.flatMap((op) => tree.apply(op));
}
